import { z } from 'zod';
import { macroVisualDigestSchema, visualAnalysisErrorCodeSchema, VISUAL_COMPARISON_LIMIT } from './common';
import { macroTriggerSchema } from '../macroTree';

export const analysisTargetVariantSchema = z.enum(['draft', 'locked', 'source-graph']);
export const analysisTargetSchema = z.object({
  campaignId: z.string().min(1).max(128),
  trigger: macroTriggerSchema,
  variant: analysisTargetVariantSchema,
  rev: z.number().int().nonnegative(),
}).strict();
export const analysisExecutionHostSchema = z.object({
  mode: z.enum(['local', 'remote']),
  machineId: z.string().min(1).max(128),
}).strict();
export const startAnalysisSchema = analysisTargetSchema.extend({
  idempotencyKey: z.string().min(1).max(128),
  /** Skip exact work-plan reuse and gather fresh GPU evidence (kernel upgrades, suspected stale artifacts). */
  recompute: z.boolean().optional(),
  executionHost: analysisExecutionHostSchema.optional(),
}).strict();
export const analysisStatusSchema = z.enum([
  'queued', 'waiting_for_gpu_host', 'preparing', 'running', 'paused',
  'validating', 'completed', 'failed', 'cancelled', 'superseded',
]);
export const analysisProgressSchema = z.object({
  jobId: z.string().min(1).max(128),
  target: analysisTargetSchema,
  manifestDigest: macroVisualDigestSchema,
  status: analysisStatusSchema,
  expectedBindings: z.number().int().nonnegative().max(16_384),
  completedBindings: z.number().int().nonnegative().max(16_384),
  casesDone: z.number().int().nonnegative().max(VISUAL_COMPARISON_LIMIT),
  casesTotal: z.number().int().nonnegative().max(VISUAL_COMPARISON_LIMIT),
  artifactDigest: macroVisualDigestSchema.optional(),
  errorCode: visualAnalysisErrorCodeSchema.optional(),
  executionHost: analysisExecutionHostSchema.optional(),
}).strict().superRefine((value, context) => {
  if (value.completedBindings > value.expectedBindings || value.casesDone > value.casesTotal) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Analysis progress exceeds declared totals' });
  }
});

export const leaseSchema = z.object({
  jobId: z.string().min(1).max(128),
  manifestDigest: macroVisualDigestSchema,
  workPlanDigest: macroVisualDigestSchema,
  hostId: z.string().min(1).max(128),
  socketId: z.string().min(1).max(128),
  epoch: z.number().int().nonnegative(),
  renewalSequence: z.number().int().nonnegative(),
  remainingMs: z.number().int().positive().max(20_000),
}).strict();
export const chunkReceiptSchema = z.object({
  sequence: z.number().int().nonnegative().max(8191),
  digest: macroVisualDigestSchema,
}).strict();
export const blobAssetSchema = z.object({
  objectKey: z.string().min(1).max(512),
  digest: macroVisualDigestSchema,
  byteCount: z.number().int().positive().max(26_214_400),
  contentType: z.string().min(1).max(128),
}).strict();
/** Lossless immutable source PNGs can exceed generic metadata/derived-asset bounds at supported resolutions. */
export const SOURCE_IMAGE_MAX_BYTES = 128 * 1024 * 1024;
/** Aggregate budget for immutable full-resolution source PNGs in one verified closure. */
export const SOURCE_IMAGE_CLOSURE_MAX_BYTES = 1024 * 1024 * 1024;
/** Aggregate budget for descriptors, reports, reference pages, and other non-source evidence. */
export const GENERIC_EVIDENCE_CLOSURE_MAX_BYTES = 256 * 1024 * 1024;
/** Coarse pre-verification fence; authenticated role-specific budgets remain authoritative. */
export const EVIDENCE_CLOSURE_MAX_BYTES = SOURCE_IMAGE_CLOSURE_MAX_BYTES + GENERIC_EVIDENCE_CLOSURE_MAX_BYTES;
export const sourceImageAssetSchema = blobAssetSchema.extend({
  byteCount: z.number().int().positive().max(SOURCE_IMAGE_MAX_BYTES),
  contentType: blobAssetSchema.shape.contentType.refine((value): boolean => value === 'image/png', 'Source image requires image/png'),
}).strict();
export const readGrantSchema = blobAssetSchema.extend({
  url: z.string().url().max(2048),
  expiresAtUnixMs: z.number().int().positive(),
}).strict();
export const sourceImageReadGrantSchema = sourceImageAssetSchema.extend({
  url: z.string().url().max(2048), expiresAtUnixMs: z.number().int().positive(),
}).strict();
export const writeGrantSchema = z.object({
  objectKey: z.string().min(1).max(512),
  url: z.string().url().max(2048),
  maxBytes: z.number().int().positive().max(26_214_400),
  expiresAtUnixMs: z.number().int().positive(),
}).strict();

export type AnalysisTarget = z.infer<typeof analysisTargetSchema>;
export type AnalysisExecutionHost = z.infer<typeof analysisExecutionHostSchema>;
export type StartAnalysis = z.infer<typeof startAnalysisSchema>;
export type AnalysisProgress = z.infer<typeof analysisProgressSchema>;
/** `GET /:campaignId/macro-analysis-jobs?trigger&variant&rev` — the job the target's current request created, if any. */
export const analysisCurrentJobSchema = z.object({
  progress: analysisProgressSchema.nullable(),
  /** Why the request produced no job: an analysis code or a preparation code such as `analysis_preparation_failed`. */
  errorCode: z.string().min(1).max(128).optional(),
}).strict();
export type AnalysisCurrentJob = z.infer<typeof analysisCurrentJobSchema>;
export type Lease = z.infer<typeof leaseSchema>;
export type ChunkReceipt = z.infer<typeof chunkReceiptSchema>;
export type BlobAsset = z.infer<typeof blobAssetSchema>;
export type ReadGrant = z.infer<typeof readGrantSchema>;
export type WriteGrant = z.infer<typeof writeGrantSchema>;
